<template>
    <v-card>
        <v-card-title class="d-flex justify-space-between align-center pa-4">
            <span>Duck Details</span>
            <v-btn
                icon="mdi-close"
                variant="text"
                density="comfortable"
                class="rounded-circle"
                :width="32"
                :height="32"
                :min-width="32"
                @click="$emit('close')"
            />
        </v-card-title>

        <v-card-text class="pa-4">
            <div class="flex flex-col items-center gap-4">
                <!-- Duck Image Container -->
                <div class="w-[200px] h-[200px]">
                    <v-img
                        :src="duckImageUrl"
                        width="200"
                        height="200"
                        class="rounded-lg"
                        cover
                    >
                        <template v-slot:placeholder>
                            <div class="w-[200px] h-[200px] bg-gray-200 rounded-lg flex items-center justify-center">
                                <v-progress-circular indeterminate />
                            </div>
                        </template>
                    </v-img>
                </div>

                <!-- Duck Information -->
                <div class="w-full space-y-3 mt-4">
                    <!-- Header with Duck ID and Rarity -->
                    <div class="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <div class="flex items-center gap-2">
                            <span class="font-semibold text-lg">Duck #{{ duck.id }}</span>
                            <v-chip
                                v-if="duck.rarity"
                                :color="getRarityColor(duck.rarity)"
                                size="small"
                                variant="elevated"
                                class="ml-1"
                            >
                                <v-icon :icon="getRarityIcon(duck.rarity)" size="14" class="mr-1"></v-icon>
                                {{ duck.rarity }}
                            </v-chip>
                        </div>
                        <v-chip
                            :color="duck.found ? 'success' : 'error'"
                            size="small"
                        >
                            {{ duck.found ? 'Found' : 'Hidden' }}
                        </v-chip>
                    </div>

                    <!-- UUID Information -->
                    <div class="flex justify-between items-center p-2 bg-gray-25 rounded">
                        <span class="font-medium text-gray-600">UUID:</span>
                        <span class="font-mono text-sm text-gray-800">{{ duck.uuid }}</span>
                    </div>
                    
                    <!-- Action Buttons -->
                    <div class="flex gap-2 mt-4">
                        <v-btn
                            v-if="duck.found"
                            block
                            color="error"
                            variant="outlined"
                            prepend-icon="mdi-eye-off"
                            :loading="hidingDuck"
                            @click="resetDuck"
                        >
                            Hide Duck
                        </v-btn>
                    </div>
                </div>
            </div>
        </v-card-text>

        <!-- Loading Overlay -->
        <v-overlay
            :model-value="loading"
            class="align-center justify-center"
            contained
        >
            <v-progress-circular indeterminate />
        </v-overlay>

        <!-- Notification Snackbar -->
        <v-snackbar
            v-model="showNotification"
            :color="notificationColor"
            :timeout="3000"
            location="top"
            class="text-center"
        >
            {{ notificationText }}
            <template v-slot:actions>
                <v-btn
                    variant="text"
                    @click="showNotification = false"
                >
                    Close
                </v-btn>
            </template>
        </v-snackbar>
    </v-card>
</template>

<style scoped>
/* Fix file input visibility */
:deep(.v-field__input) {
    min-height: 44px !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

/* Fix button and input heights */
:deep(.v-input) {
    min-height: 56px !important;
}

:deep(.v-field__input) {
    min-height: 56px !important;
    padding: 0 16px !important;
}

:deep(.v-btn) {
    min-height: 56px !important;
}

/* Ensure close button is perfectly round */
:deep(.v-btn--icon.rounded-circle) {
    border-radius: 50% !important;
    padding: 0 !important;
    width: 32px !important;
    height: 32px !important;
    min-width: 32px !important;
    aspect-ratio: 1/1 !important;
}

/* Enhanced rarity chip styling */
:deep(.v-chip.v-chip--elevated) {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15) !important;
    font-weight: 600 !important;
    transition: all 0.2s ease !important;
}

:deep(.v-chip.v-chip--elevated:hover) {
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2) !important;
}

:deep(.v-chip--color-green) {
    background-color: rgb(34 197 94) !important;
    color: white !important;
}

:deep(.v-chip--color-blue) {
    background-color: rgb(59 130 246) !important;
    color: white !important;
}

:deep(.v-chip--color-purple) {
    background-color: rgb(168 85 247) !important;
    color: white !important;
}

/* Enhanced header styling */
.bg-gray-50 {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
}

.bg-gray-25 {
    background-color: #f8fafc;
    border: 1px solid #e2e8f0;
}
</style>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { DetailedDuck } from '@/interfaces/cruise';
import { useDuckStore } from '@/stores/duckStore';

const props = defineProps<{
    duck: DetailedDuck;
}>();

const emit = defineEmits(['close', 'updated', 'refresh-images', 'refresh-cruises']);

const duckStore = useDuckStore();
const DEFAULT_DUCK_ICON = '/ducksImages/duckIcon.webp';
const loading = ref(false);
const showNotification = ref(false);
const notificationText = ref('');
const notificationColor = ref('');

const hidingDuck = ref(false);

// Rarity utility functions
const getRarityColor = (rarity: 'COMMON' | 'RARE' | 'LEGENDARY') => {
  switch (rarity) {
    case 'COMMON':
      return 'green';
    case 'RARE':
      return 'blue';
    case 'LEGENDARY':
      return 'purple';
    default:
      return 'grey';
  }
};

const getRarityIcon = (rarity: 'COMMON' | 'RARE' | 'LEGENDARY') => {
  switch (rarity) {
    case 'COMMON':
      return 'mdi-star';
    case 'RARE':
      return 'mdi-star-half-full';
    case 'LEGENDARY':
      return 'mdi-star-box';
    default:
      return 'mdi-help';
  }
};

// Handle duck image - now supports rarity-based images
const duckImageUrl = computed(() => {
  // If duck has rarity, use rarity-based image
  if (props.duck.rarity) {
    return `/duckRarity/${props.duck.rarity}.webp`;
  }
  
  // Fallback to individual duck image or default
  if (props.duck.image) {
    return props.duck.image.startsWith('data:') 
      ? props.duck.image 
      : `data:image/webp;base64,${props.duck.image}`;
  }
  
  return DEFAULT_DUCK_ICON;
});

const resetDuck = async () => {
    try {
        hidingDuck.value = true;
        // Use duckStore.hideDuck instead of direct API call to ensure local state is updated
        await duckStore.hideDuck(props.duck.uuid);
        
        // Update the duck's status in the current component
        props.duck.found = 0;
        
        notificationText.value = 'Duck hidden successfully';
        notificationColor.value = 'success';
        showNotification.value = true;
        
        // Emit events to update UI
        emit('updated');
        emit('refresh-cruises');
        emit('close');
    } catch (error) {
        console.error('Failed to hide duck:', error);
        notificationText.value = 'Failed to hide duck';
        notificationColor.value = 'error';
        showNotification.value = true;
    } finally {
        hidingDuck.value = false;
    }
};
</script>
